import React from 'react';
import { compose, is, head, path, replace } from 'ramda';
import Translate from 'react-translate-component';

import { DefinitionField } from 'components/common/fields/DefinitionField';
import { DefinitionHeader } from 'components/common/fields/DefinitionHeader';
import { notEmpty } from 'utils/ramdaAdditions';
import HighLightedLinks from './HighlightedLinks';

const formatXMLText = compose(
    replace(/&amp;/g, '&'),
    replace(/&quot;/g, '"'),
    replace(/&lt;/g, '<'),
    replace(/&gt;/g, '>'),
    replace(/&#47;/g, '/'),
    replace(/&#39;/g, '\''),
);

const getProp = (value) => {
    const result = is(Array, value) && head(value);

    return is(String, result) ? formatXMLText(result) : result;
}

const isSection = (target) => target === 'sections';

const getEmailString = (value) => {
    const item = path(['0', 'item', '0'], value);
    return item ? [head(item.email) + (item.email_comm ? head(item.email_comm) : '')] : null;
};

const Experience = ({ value: experience }) => notEmpty(experience) ? (
    <div className="xml-section">
        <DefinitionHeader translate="xml.resume.experience" />
        <div className="panel-view">
            { experience.map((item, i) => (
                <div key={i} className="xml-section-item">
                    <div className="xml-header">
                        { getProp(item.exp_date) ? (
                            <div className="xml-period">
                                {getProp(item.exp_date)}
                            </div>
                        ) : null}
                        { getProp(item.exp_date_from) || getProp(item.exp_date_to) ? (
                            <div className="xml-period">
                                {`${getProp(item.exp_date_from)} — ${getProp(item.exp_date_to)}`}
                            </div>
                        ) : null}
                        <h4>{getProp(item.exp_org)}, <a href={getProp(item.exp_url) || undefined}>{getProp(item.exp_url)}</a></h4>
                        <div className="xml-position">{getProp(item.exp_dolg)}</div>
                    </div>
                    <p className="pre-wrap">
                        <HighLightedLinks text={getProp(item.exp_desc)} />
                    </p>
                    <div className="divider"/>
                </div>
            )) }
        </div>
    </div>
) : null;

const Languages = ({ value: languages }) => notEmpty(languages) ? (
    <div className="xml-section">
        <DefinitionHeader translate="xml.resume.language" />
        <ul className="xml-section-item panel-view pre-wrap">
            { languages.map((language, i) => (
                <li key={i}>{getProp(language.lang_name)} — {getProp(language.lang_level)}</li>
            )) }
        </ul>
    </div>
) : null;

const Education = ({ value: education, addProp: edu_short }) => notEmpty(education) ? (
    <div className="xml-section">
        <DefinitionHeader translate="xml.resume.education" addContent={ getProp(edu_short) && ` - ${getProp(edu_short)}`} />
        <div className="panel-view">
            { education.map((item, i) => (
                <div key={i} className="xml-section-item">
                    <div className="xml-header">
                        <div className="xml-period">{getProp(item.edu_date_to)}</div>
                        <h4>{getProp(item.edu_org)}</h4>
                        <div className="xml-position">{getProp(item.edu_name)}</div>
                    </div>
                    <p className="pre-wrap">
                        <HighLightedLinks text={getProp(item.edu_desc)} />
                    </p>
                    <div className="divider"/>
                </div>
            )) }
        </div>
    </div>
) : null;

const SimpleSection = ({ value, name }) => notEmpty(value) ? (
    <div className="xml-section">
        <DefinitionHeader translate={`xml.resume.${name}`} />
        <div className="xml-section-item panel-view pre-wrap">
            <HighLightedLinks text={getProp(value)} />
        </div>
    </div>
) : null;

const Photo = ({ value: src }) => notEmpty(src) ? (
    <img className="xml-photo" src={'data:image/jpeg;base64,' + getProp(src)} alt=""/>
) : null;

const XmlTableRow = ({ value, name }) => (
    <DefinitionField translate={`xml.resume.${name}`} text={getProp(value)} />
);

const Messenger = ({ value }) => {
    const item = path(['0', 'item', '0'], value);
    return item && is(Object, item) ? <XmlTableRow value={[`${head(item.im_type)}: ${head(item.im)}`]} name="im" /> : null;
};

const LastUpdate = ({ value }) => notEmpty(value) ? (
    <span className="xml-last-update"><Translate content="xml.resume.lastupdate" /> {getProp(value)}</span>
) : null;

const WaitStatus = ({ value, addProp }) => (
    <div className="xml-waitpos panel-view">
        <DefinitionField translate="xml.resume.wait_status" text={getProp(value)} textClassName={path([0], addProp) ? 'with-photo' : ''} />
    </div>
);

const WaitMoney = ({ value, addProp }) => (
    <div className="xml-waitpos panel-view">
        <DefinitionField translate="xml.resume.wait_money" text={getProp(value)} textClassName={path([0], addProp) ? 'with-photo' : ''} />
    </div>
);

const OwnUrl = ({ value, addProp }) => (
    <DefinitionField translate="xml.resume.own_url" textClassName={path([0], addProp) ? 'with-photo' : ''}>
        { value.map((item, i) => (
            <span key={i}>
                <span>
                    <a href={item}>{item}</a>
                </span>
                <br/>
            </span>
        )) }
    </DefinitionField>
);

const Phone = ({ value }) => <XmlTableRow value={path(['0', 'item', '0', 'phone'], value)} name="phone" />;

const Email = ({ value }) => <XmlTableRow value={getEmailString(value)} name="email" />;

export const templates = {
    'photo':        { template: Photo,         target: 'photo' },
    'about':        { template: SimpleSection, target: 'sections' },
    'email':        { template: Email,         target: 'fields' },
    'education':    { template: Education,     target: 'sections', addProp: 'edu_short' },
    'addinfo':      { template: SimpleSection, target: 'sections' },
    'address':      { template: XmlTableRow,   target: 'fields' },
    'birthday':     { template: XmlTableRow,   target: 'fields' },
    'businesstrip': { template: XmlTableRow,   target: 'fields' },
    'citizen':      { template: XmlTableRow,   target: 'fields' },
    'edu_short':    { skip: true },
    'experience':   { template: Experience,    target: 'sections' },
    'first_name':   { template: XmlTableRow,   target: 'fields' },
    'im':           { template: Messenger,     target: 'fields' },
    'language':     { template: Languages,     target: 'sections' },
    'last_name':    { template: XmlTableRow,   target: 'fields' },
    'lastupdate':   { template: LastUpdate,    target: 'lastUpdate' },
    'metro':        { template: XmlTableRow,   target: 'fields' },
    'middle_name':  { template: XmlTableRow,   target: 'fields' },
    'own_url':      { template: OwnUrl,        target: 'fields', addProp: 'photo' },
    'phone':        { template: Phone,         target: 'fields' },
    'relocation':   { template: XmlTableRow,   target: 'fields' },
    'sex':          { template: XmlTableRow,   target: 'fields' },
    'siteid':       { skip: true },
    'ver':          { skip: true },
    'skill':        { template: SimpleSection, target: 'sections' },
    'wait_status':  { template: WaitStatus,   target: 'wait_status', addProp: 'photo' },
    'wait_money':  { template: WaitMoney,   target: 'wait_money', addProp: 'photo' },
};

export const getItem = (value, key, addProp) => {
    const Component = templates[key].template;
    const sectionValue = path(['0', 'item'], value);

    return isSection(templates[key].target) ?
        <Component value={sectionValue ? sectionValue : value} key={key} name={key} addProp={addProp} /> :
        <Component value={value} key={key} name={key} addProp={addProp}  />;
};
